import {useCallback, useMemo} from 'react'
import {useSearchParams} from 'react-router'

const SELECTED_NODES_PARAM = 'nodes'

export const useSelectedNodesQueryParams = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const selectedNodeIds = useMemo<string[]>(() => {
    const param = searchParams.get(SELECTED_NODES_PARAM)
    if (!param) return []
    return param.split(',').filter(Boolean)
  }, [searchParams])

  const setSelectedNodeIds = useCallback(
    (nodeIds: string[]) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (nodeIds.length === 0) {
            next.delete(SELECTED_NODES_PARAM)
          } else {
            next.set(SELECTED_NODES_PARAM, nodeIds.join(','))
          }
          return next
        },
        {replace: true}
      )
    },
    [setSearchParams]
  )

  const addNodeIds = useCallback(
    (nodeIds: (string | number)[]) => {
      const newIds = nodeIds.map(String)
      const merged = [...new Set([...selectedNodeIds, ...newIds])]
      setSelectedNodeIds(merged)
    },
    [selectedNodeIds, setSelectedNodeIds]
  )

  const removeNodeId = useCallback(
    (nodeId: string) => {
      setSelectedNodeIds(selectedNodeIds.filter((id) => id !== nodeId))
    },
    [selectedNodeIds, setSelectedNodeIds]
  )

  const clearNodeIds = useCallback(() => {
    setSelectedNodeIds([])
  }, [setSelectedNodeIds])

  return {selectedNodeIds, setSelectedNodeIds, addNodeIds, removeNodeId, clearNodeIds}
}
